// D3 Horizon Chart for BodyOS data visualizations
// Compact visualization for comparing multiple time series (HRV, glucose patterns, etc.)
import * as d3 from 'd3'
import {
  colors,
  fonts,
  animationConfig,
  defaultDimensions
} from './config'
import {
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  debounce
} from './utils'

export default class HorizonChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: 400,
      margins: { top: 30, right: 20, bottom: 40, left: 120 },
      bands: 4, // Number of horizon bands
      bandHeight: 30, // Height of each series row
      xLabel: '',
      showLegend: false,
      animate: true,
      responsive: true,
      mode: 'offset', // 'offset' or 'mirror'
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  render() {
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)

    // Calculate height based on number of series
    const numSeries = this.data.length
    const rowHeight = this.options.bandHeight + 10
    const height = this.options.margins.top + this.options.margins.bottom + (numSeries * rowHeight)

    const { margins, bands } = this.options

    // Create SVG
    const svg = d3.select(this.container)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('class', 'd3-visualization')
      .style('width', '100%')
      .style('height', 'auto')
      .style('font-family', fonts.family)

    this.svg = svg

    const g = svg.append('g')
      .attr('transform', `translate(${margins.left}, ${margins.top})`)

    this.tooltip = createTooltip(this.container)

    const innerWidth = width - margins.left - margins.right

    // Get all x values (assuming time series)
    const allXValues = this.data[0].values.map((d) => d.x)

    // X scale
    const xScale = d3.scalePoint()
      .domain(allXValues)
      .range([0, innerWidth])

    // Calculate global min/max for consistent scaling
    const allValues = this.data.flatMap((s) => s.values.map((v) => v.y))
    const yMax = d3.max(allValues)
    const yMin = d3.min(allValues)
    const yExtent = Math.max(Math.abs(yMax), Math.abs(yMin))

    // Color scale for bands
    const positiveColors = [
      colors.neutral[200],
      colors.neutral[400],
      colors.neutral[600],
      colors.neutral[900]
    ]
    const _negativeColors = [
      '#fecaca', // red-200
      '#f87171', // red-400
      '#dc2626', // red-600
      '#991b1b' // red-900
    ]

    // Draw each series
    this.data.forEach((series, seriesIndex) => {
      const yOffset = seriesIndex * rowHeight
      const bandHeightPx = this.options.bandHeight

      // Create group for this series
      const seriesGroup = g.append('g')
        .attr('class', `horizon-series horizon-series-${seriesIndex}`)
        .attr('transform', `translate(0, ${yOffset})`)

      // Add series label
      seriesGroup.append('text')
        .attr('x', -10)
        .attr('y', bandHeightPx / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.sm)
        .attr('font-weight', fonts.weights.medium)
        .text(series.name)

      // Background
      seriesGroup.append('rect')
        .attr('width', innerWidth)
        .attr('height', bandHeightPx)
        .attr('fill', colors.neutral[50])
        .attr('stroke', colors.neutral[200])
        .attr('stroke-width', 1)

      // Draw horizon bands
      const yScale = d3.scaleLinear()
        .domain([0, yExtent / bands])
        .range([bandHeightPx, 0])

      const area = d3.area()
        .defined((d) => d.y !== null && d.y !== undefined)
        .x((d) => xScale(d.x))
        .y0(bandHeightPx)
        .y1((d) => yScale(Math.max(0, d.y % (yExtent / bands))))
        .curve(d3.curveMonotoneX)

      // Create clipping path
      const clipId = `horizon-clip-${seriesIndex}`
      svg.append('defs')
        .append('clipPath')
        .attr('id', clipId)
        .append('rect')
        .attr('width', innerWidth)
        .attr('height', bandHeightPx)

      // Draw positive bands
      for (let band = 0; band < bands; band += 1) {
        const bandData = series.values.map((d) => {
          const bandValue = d.y - (band * yExtent / bands)
          return {
            x: d.x,
            y: Math.min(Math.max(bandValue, 0), yExtent / bands)
          }
        })

        seriesGroup.append('path')
          .datum(bandData)
          .attr('class', `horizon-band horizon-band-${band}`)
          .attr('d', area)
          .attr('fill', positiveColors[band] || positiveColors[positiveColors.length - 1])
          .attr('clip-path', `url(#${clipId})`)
          .attr('opacity', this.options.animate ? 0 : 1)
      }

      // Add interaction overlay
      seriesGroup.append('rect')
        .attr('class', 'horizon-overlay')
        .attr('width', innerWidth)
        .attr('height', bandHeightPx)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event) => this.handleMouseMove(event, series, xScale, innerWidth))
        .on('mouseout', () => hideTooltip(this.tooltip))
    })

    // Animate bands
    if (this.options.animate) {
      g.selectAll('.horizon-band')
        .transition()
        .duration(animationConfig.duration)
        .delay((d, i) => i * 50)
        .attr('opacity', 1)
    }

    // X axis
    const xAxis = g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${numSeries * rowHeight})`)
      .call(d3.axisBottom(xScale).tickValues(allXValues.filter((_, i) => i % Math.ceil(allXValues.length / 8) === 0)))

    xAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    xAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 1)

    if (this.options.xLabel) {
      g.append('text')
        .attr('x', innerWidth / 2)
        .attr('y', (numSeries * rowHeight) + 35)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.base)
        .attr('font-weight', fonts.weights.medium)
        .text(this.options.xLabel.toUpperCase())
    }

    return this
  }

  handleMouseMove(event, series, xScale, innerWidth) {
    const [mouseX] = d3.pointer(event)
    const xDomain = xScale.domain()
    const step = innerWidth / (xDomain.length - 1)
    const index = Math.round(mouseX / step)

    if (index >= 0 && index < series.values.length) {
      const point = series.values[index]
      const formatValue = this.options.formatValue || ((v) => v.toFixed(1))

      const content = `
        <strong>${series.name}</strong><br/>
        <span style="color: ${colors.textMuted}">${point.x}</span><br/>
        <span style="color: ${colors.accent}">${formatValue(point.y)}</span>
      `
      showTooltipHtml(this.tooltip, content, event)
    }
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
